/**
 * CascadePreviewModal — preview-and-confirm dialog used by every
 * schedule edit that triggers a cascade.
 *
 * Flow:
 *   1. Host calls openPreview(payload) which POSTs to /cascade/preview.
 *   2. Modal renders the structured diff (rescheduled / removed / added).
 *   3. User clicks Confirm → modal POSTs /cascade/apply with the
 *      preview_token and the same payload. On success it fires
 *      onApplied({ audit_log_id, undo_expires_at }).
 *   4. Host shows an Undo toast for 30s using audit_log_id.
 *
 * The host is responsible for rendering the Undo toast — keeping
 * Modal stateless after Apply means it can also be reused for
 * server-side cascades the user didn't initiate (e.g., template edits).
 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { authFetch } from "@/lib/auth";
import { toast } from "sonner";

export interface CascadeChangePayload {
  cascade_type:
    | "task_reschedule"
    | "planting_reschedule"
    | "template_edit"
    | "rotation_shift"
    | "vaccination_followup";
  trigger_entity_type: string;
  trigger_entity_id: string;
  change: Record<string, unknown>;
}

interface AffectedInstance {
  id: string;
  source_table: string;
  title: string;
  before_date: string | null;
  after_date: string | null;
  change: "rescheduled" | "removed" | "added";
}

interface CascadePreview {
  preview_token: string;
  cascade_type: string;
  summary: {
    total_affected: number;
    rescheduled_count: number;
    removed_count: number;
    added_count: number;
  };
  affected_instances: AffectedInstance[];
  expires_at: string;
}

export interface CascadeApplyResult {
  audit_log_id: string;
  affected_count: number;
  undo_expires_at: string;
}

interface Props {
  open: boolean;
  payload: CascadeChangePayload | null;
  /**
   * Called when user closes the modal without applying. Includes both
   * Cancel button and ESC/backdrop dismissal.
   */
  onClose: () => void;
  /**
   * Called once the apply succeeds. Host should refresh data and
   * surface an Undo toast.
   */
  onApplied: (result: CascadeApplyResult) => void;
}

const CASCADE_LABEL: Record<string, string> = {
  task_reschedule: "Task reschedule",
  planting_reschedule: "Planting reschedule",
  template_edit: "Template edit",
  rotation_shift: "Rotation shift",
  vaccination_followup: "Vaccination follow-up",
};

export function CascadePreviewModal({ open, payload, onClose, onApplied }: Props): JSX.Element {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState<CascadePreview | null>(null);
  const [expanded, setExpanded] = useState(false);

  // Fetch preview when modal opens
  useEffect(() => {
    if (!open || !payload) return;
    setLoading(true);
    setError("");
    setPreview(null);
    setExpanded(false);
    (async () => {
      try {
        const data = await authFetch<CascadePreview>("/api/cascade/preview", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        setPreview(data);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Failed to compute preview";
        setError(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, payload]);

  const handleConfirm = async () => {
    if (!preview || !payload) return;
    setApplying(true);
    try {
      const result = await authFetch<CascadeApplyResult>("/api/cascade/apply", {
        method: "POST",
        body: JSON.stringify({
          ...payload,
          preview_token: preview.preview_token,
        }),
      });
      onApplied(result);
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Apply failed";
      toast.error(msg);
      setError(msg);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {payload ? CASCADE_LABEL[payload.cascade_type] ?? "Schedule change" : "Schedule change"}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            Computing the change…
          </p>
        ) : error ? (
          <p className="text-sm text-clay dark:text-clay-dark py-4">{error}</p>
        ) : preview ? (
          <div className="space-y-4">
            {/* Summary */}
            <div className="bg-surface-raised dark:bg-surface-raised-dark rounded-lg p-4">
              <p className="text-sm">
                <span className="font-semibold">{preview.summary.total_affected}</span>
                {" task instance"}
                {preview.summary.total_affected === 1 ? "" : "s"}
                {" will be affected by this change."}
              </p>
              {preview.summary.rescheduled_count > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {preview.summary.rescheduled_count} rescheduled
                  {preview.summary.removed_count > 0 && `, ${preview.summary.removed_count} removed`}
                  {preview.summary.added_count > 0 && `, ${preview.summary.added_count} added`}
                </p>
              )}
            </div>

            {/* Detail rows (expandable) */}
            {preview.affected_instances.length > 0 && (
              <div>
                <button
                  onClick={() => setExpanded((x) => !x)}
                  className="text-xs text-muted-foreground underline mb-2"
                >
                  {expanded ? "Hide details" : `Show ${preview.affected_instances.length} affected instances`}
                </button>
                {expanded && (
                  <div className="space-y-1 max-h-64 overflow-y-auto rounded border border-token-border dark:border-token-border-dark bg-surface-raised dark:bg-surface-raised-dark">
                    {preview.affected_instances.map((ai, i) => (
                      <div
                        key={ai.id}
                        className={`flex justify-between items-center px-3 py-2 text-xs ${
                          i % 2 === 0 ? "bg-white" : "bg-surface-raised dark:bg-surface-raised-dark"
                        }`}
                      >
                        <span className="font-medium truncate flex-1">{ai.title}</span>
                        <span className="text-muted-foreground line-through ml-2">
                          {ai.before_date ? ai.before_date.split("T")[0] : "—"}
                        </span>
                        <span className="mx-2 text-muted-foreground">→</span>
                        <span className="font-semibold text-moss dark:text-moss-dark">
                          {ai.after_date ? ai.after_date.split("T")[0] : "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {preview.summary.total_affected === 0 && (
              <p className="text-sm text-muted-foreground text-center py-2">
                This change has no downstream impact. You can apply it directly.
              </p>
            )}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={applying}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={applying || loading || !preview}
            className="bg-burnt-orange hover:bg-burnt-orange/90 dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90 text-white"
          >
            {applying ? "Applying…" : "Confirm change"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
